import cn from 'classnames';
import { Button } from '@ox/blox';
import type { JSX } from 'preact';
import type { HTMLAttributes } from '../../types/utility';
import type { IconType } from '../../types/icons';

interface BaseProps {
  as?: keyof JSX.IntrinsicElements;
  emphasis?: 'low' | 'high';
  icon?: IconType;
  theme?: 'default' | 'highlight' | 'positive' | 'negative' | 'warning' | 'none';
  type?: 'anchor' | 'selectable' | 'removable';
}

type Props = BaseProps &
  (
    | ({ as?: 'a'; type?: 'anchor'; href?: string } & HTMLAttributes<HTMLAnchorElement>)
    | ({ as?: 'button'; type?: 'selectable' | 'removable' } & HTMLAttributes<HTMLButtonElement>)
    | HTMLAttributes<HTMLElement>
  );

export default function Tag({
  as,
  type,
  children,
  className,
  icon,
  theme = 'default',
  emphasis = 'low',
  onClick,
  ...props
}: Props) {
  const classList = cn('tag', className, {
    [`theme-${theme}`]: theme !== 'default',
    [`emphasis-${emphasis}`]: emphasis !== 'low',
  });
  let Element = as ?? 'span';

  // @ts-ignore href can exist
  if (as === 'a' || type === 'anchor' || props.href) {
    Element = 'a';
  } else if (as === 'button' || type === 'selectable') {
    Element = type === 'removable' ? 'span' : 'button';
  }

  let iconToUse;

  if (Element === 'a') {
    iconToUse = 'chevron-right';
  } else if (type !== 'removable') {
    iconToUse = icon || undefined;
  }

  /* @ts-ignore TS thinks SVG-related elements could be passed in */
  return (
    <Element
      className={classList}
      data-icon={iconToUse}
      /* @ts-ignore onClick is only valid for <button> element */
      onClick={type !== 'removable' ? onClick : undefined}
      {...props}
    >
      {children}
      {type === 'removable' && (
        <Button
          hierarchy="tertiary"
          aria-label={`Remove ${children}`}
          icon="close"
          /* @ts-ignore value can exist */
          value={props.value}
          isIconOnly
          onClick={onClick}
        />
      )}
    </Element>
  );
}

// @ts-ignore the union is too large to represent
type HelperProps = Omit<Props, 'theme'>;

export function PositiveTag(props: HelperProps) {
  return <Tag {...props} theme="positive" />;
}

export function NegativeTag(props: HelperProps) {
  return <Tag {...props} theme="negative" />;
}

export function WarningTag(props: HelperProps) {
  return <Tag {...props} theme="warning" />;
}

export function HighlightTag(props: HelperProps) {
  return <Tag {...props} theme="highlight" />;
}
